import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/20/solid';
import { Heading, Subheading } from '../../../../components/Heading';
import { Card } from '../../../../components/Card';
import { Input } from '../../../../components/Input';
import { Button } from '../../../../components/Button';
import showLoadingToast from '../../../../components/Toasts/ShowLoadingToast';
import showSuccessToast from '../../../../components/Toasts/ShowSuccessToast';
import showErrorToast from '../../../../components/Toasts/ShowErrorToast';
import { useAiReportCustomerProfiles } from '../../../../hooks/useAiReportCustomerProfile';
import { CUSTOMER_PROFILE_FORM_FIELDS } from '../formFields';

const emptyProfile: Record<string, string> = Object.fromEntries(
    CUSTOMER_PROFILE_FORM_FIELDS.map((f) => [f.name, ''])
);

const AddCustomerProfilePage = () => {
    const navigate = useNavigate();
    const [profile, setProfile] = useState<Record<string, string>>(emptyProfile);
    const { addProfile } = useAiReportCustomerProfiles();

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setProfile((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!profile.customer_name.trim()) {
            showErrorToast('Customer Name is required');
            return;
        }
        try {
            await addProfile(profile as any);
            showLoadingToast(`Creating ${profile.customer_name} ...`, 100);
            setTimeout(() => {
                navigate('/gp/ai-report-helper/customer-profiles');
                showSuccessToast(`${profile.customer_name} added successfully`);
            }, 1000);
        } catch (err) {
            showErrorToast(`Failed to add customer profile: ${err}`);
        }
    };

    return (
        <form method="post" onSubmit={handleSubmit} className="mx-auto max-w-6xl">
            <div className="lg:flex lg:items-center lg:justify-between mb-5">
                <div className="flex items-center gap-3">
                    <button
                        type="button"
                        onClick={() => navigate('/gp/ai-report-helper/customer-profiles')}
                        className="text-zinc-400 hover:text-zinc-950 dark:hover:text-white"
                        aria-label="Back"
                    >
                        <ArrowLeftIcon className="size-5" />
                    </button>
                    <Heading>Add Customer Profile</Heading>
                </div>
                <div className='mt-5 flex lg:ml-4 lg:mt-0'>
                    <Button type="reset" outline onClick={() => navigate(-1)}>
                        Cancel
                    </Button>
                    <Button type="submit" className='ml-3'>Save</Button>
                </div>
            </div>
            <Card className="mt-6 p-6">
                <div className="grid grid-cols-1 gap-x-8 gap-y-10 md:grid-cols-3">
                    <div>
                        <h2 className="text-base font-semibold leading-7 text-foreground">Customer Profile</h2>
                        <p className="mt-1 text-sm leading-6 text-muted-foreground">
                            Constant details for this customer, used to auto-fill report columns
                            (e.g. District, State) that never appear in the source report.
                        </p>
                    </div>

                    <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
                        {CUSTOMER_PROFILE_FORM_FIELDS.map((field) => (
                            <div className="sm:col-span-full" key={field.name}>
                                <section>
                                    <div className="space-y-1">
                                        <Subheading>{field.label}</Subheading>
                                    </div>
                                    <div className="mt-2">
                                        <Input
                                            aria-label={field.label}
                                            name={field.name}
                                            value={profile[field.name]}
                                            onChange={handleChange}
                                        />
                                    </div>
                                </section>
                            </div>
                        ))}
                    </div>
                </div>
            </Card>
            <div className="flex justify-end gap-4 my-6">
                <Button type="reset" plain onClick={() => navigate(-1)}>
                    Cancel
                </Button>
                <Button type="submit">Save</Button>
            </div>
        </form>
    );
};

export default AddCustomerProfilePage;
